import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState, useMemo } from "react";
import {
  Alert,
  Button,
  Row,
  OverlayTrigger,
  Tooltip,
  Form,
  Card,
  Col,
  Badge,
} from "react-bootstrap";
import { connect, useSelector } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router-dom";
import {
  DocuClipperBillingCycle,
  DocuClipperPlan,
  StripePlan,
} from "../../../../docuclipper/DocuclipperTypes";
import { ReduxState } from "../../../../redux";
import { createAlert, CreateAlertFn } from "../../../../redux/reducers/Alerts";
import { sendPageViewToBackend } from "../../../../docuclipper/api";
import {
  groupPlansByTiers,
  getTierPrice,
  getPlanFeatures,
  calculateSavingsPercentage,
  PlanTierGroup,
} from "./tierUtils";
import { PDFPageCounter } from "../../Pricing/PDFPageCounter";

type Props = {
  plans: StripePlan[];
  onSelect: (plan: StripePlan, tierIndices: Record<string, number>) => any;
  currentPlan: null | DocuClipperPlan;
  currentBillingCycle: null | DocuClipperBillingCycle;
  initialTierIndices?: Record<string, number>;
};

type ReduxProps = {
  createAlert: CreateAlertFn;
};

export const getCurrencySymbol = (currency) => {
  switch (currency) {
    case "zar":
      return "R";
    case "cad":
      return "C$";
    case "aud":
      return "A$";
    case "gbp":
      return "£";
    default:
      return "$";
  }
};

// Normalize segment values to categories
const normalizeSegment = (segment: string | undefined): string => {
  if (!segment) return "general";

  const normalized = segment.toLowerCase();

  // Accountants
  if (normalized.includes("accountant") || normalized.includes("bookkeeper")) {
    return "accountants";
  }

  // Financial Investigators
  if (
    normalized.includes("financial investigator") ||
    normalized.includes("forensic")
  ) {
    return "financial_investigators";
  }

  // Lenders
  if (
    normalized.includes("lender") ||
    normalized.includes("underwriter") ||
    normalized.includes("credit analyst")
  ) {
    return "lenders";
  }

  // Legal
  if (normalized.includes("legal") || normalized.includes("family law")) {
    return "legal";
  }

  // Business Owners
  if (
    normalized.includes("business owner") ||
    normalized.includes("founder") ||
    normalized.includes("in-house finance")
  ) {
    return "business_owners";
  }

  // Developers
  if (normalized.includes("developer") || normalized.includes("api")) {
    return "developers";
  }

  // Personal/Individual
  if (
    normalized.includes("individual") ||
    normalized.includes("personal use")
  ) {
    return "personal";
  }

  return "general";
};

// Get segment-specific heading
const getSegmentHeading = (segment: string | undefined): string => {
  const segmentKey = normalizeSegment(segment);

  const headings = {
    accountants: "Automate Client Bank Statement & Invoice Processing",
    financial_investigators:
      "Extract & Analyze Financial Documents for Investigations",
    lenders: "Streamline Loan Document Processing & Underwriting",
    legal: "Streamline Legal Document Processing & Case Management",
    business_owners: "Automate Your Business Bookkeeping & Data Entry",
    developers: "Powerful API for Document Data Extraction",
    personal: "Simplify Your Personal Financial Document Management",
    general: "Automate Data Entry from Invoices, Receipts, and Bank Statements",
  };

  return headings[segmentKey] || headings.general;
};

// Get segment-specific plan descriptions (Best For)
const getSegmentPlanDescription = (
  segment: string | undefined,
  parentPlan: string
): string => {
  const segmentKey = normalizeSegment(segment);

  const descriptions = {
    accountants: {
      Starter: "Solo practitioners & small firms (1-5 clients/month)",
      Business:
        "Growing firms with regular client workload (10-30 clients/month)",
      Enterprise: "Large firms with high-volume processing & compliance needs",
    },
    financial_investigators: {
      Starter: "Individual investigators & small cases",
      Business: "Investigation teams with regular caseload",
      Enterprise: "Large agencies with forensic & compliance requirements",
    },
    lenders: {
      Starter: "Small lending teams processing 5-15 applications/month",
      Business: "Regional lenders with 30-60 applications/month",
      Enterprise: "National lenders with high-volume & regulatory needs",
    },
    legal: {
      Starter: "Solo practitioners & small law firms",
      Business: "Mid-size firms with regular case documentation",
      Enterprise: "Large firms with high-volume & compliance requirements",
    },
    business_owners: {
      Starter: "Solo entrepreneurs & freelancers",
      Business: "Small to mid-size businesses with regular bookkeeping",
      Enterprise: "Large organizations needing advanced financial management",
    },
    developers: {
      Starter: "Development projects & small-scale integrations",
      Business: "Production apps with moderate API usage",
      Enterprise: "High-volume integrations with dedicated support",
    },
    personal: {
      Starter: "Personal finance & individual document management",
      Business: "Households with multiple income sources & investments",
      Enterprise: "Complex personal finance & estate management",
    },
    general: {
      Starter: "Small businesses, freelancers, or teams just getting started",
      Business: "Growing businesses with regular document processing needs",
      Enterprise:
        "Large organizations with high-volume & compliance requirements",
    },
  };

  return (
    descriptions[segmentKey]?.[parentPlan] || descriptions.general[parentPlan]
  );
};

export const PlanSelector2: React.FC<
  Props & ReduxProps & RouteComponentProps
> = ({
  plans,
  onSelect,
  currentPlan,
  currentBillingCycle,
  createAlert,
  history,
  location,
  initialTierIndices,
}) => {
  const { subscription } = useSelector(
    (state: ReduxState) => state.Subscription
  );
  const { user } = useSelector((state: ReduxState) => state.User);
  const coupon = subscription ? subscription.coupon : undefined;

  const [billingCycle, setBillingCycle] =
    useState<DocuClipperBillingCycle>("Annually");

  // Group plans by tiers
  const planTierGroups = useMemo(() => groupPlansByTiers(plans), [plans]);

  // Track selected tier index for each plan
  const [selectedTierIndices, setSelectedTierIndices] = useState<
    Record<string, number>
  >(() => {
    // Use initialTierIndices if provided and not empty, otherwise default to main plan
    if (initialTierIndices && Object.keys(initialTierIndices).length > 0) {
      return initialTierIndices;
    }
    const initial: Record<string, number> = {};
    planTierGroups.forEach((group) => {
      // Default all plans to middle option (index 1)
      // Starter (index 1) = "Starter" (120 pages)
      // Business (index 1) = "Business 1000" (1000 pages)
      // Enterprise (index 1) = "Enterprise 3000" (3000 pages)
      initial[group.parentPlan] = 1;
    });
    return initial;
  });

  // Testimonials data
  const testimonials = [
    {
      quote: "DocuClipper saved us a lot of time",
      text: "DocuClipper has saved us hours weekly by automating data entry with high accuracy, even for scanned documents. Their support team is quick and reliable, resolving issues the same day.",
      link: "https://www.docuclipper.com/customer-stories/sikich/",
      name: "Kristin M.",
      title: "Staff Accountant, Sikich",
      image:
        "https://www.docuclipper.com/wp-content/uploads/kristin-sikich.webp",
    },
    {
      quote: "DocuClipper Cleanly Extracted the Data",
      text: "I needed to convert my 2 year old PDF bank statements to an excel file so I could import them into Quickbooks Online. This website is amazing and converts the PDF statements not only to excel but also to Quickbooks online and desktop version. This has SAVE me money, because I don't have to pay a bookkeeper to enter the transaction manually.",
      name: "Darren M.",
      title: "Founder / CEO @ Bunker 27",
      image:
        "https://www.docuclipper.com/wp-content/uploads/Darren-M.-DocuClipper-customer.webp",
    },
    {
      quote: "DocuClipper Simplifies Importing Bank Statements",
      text: "DocuClipper is incredibly fast and efficient—just upload a bank statement from any bank, and within seconds, you get a neatly organized Excel file. It truly simplifies the process!",
      name: "Stacey G.",
      title: "CEO",
      image:
        "https://www.docuclipper.com/wp-content/uploads/Stacey-G-DocuClipper-Reviews.jpeg",
    },
  ];

  const checkCircleStyle = {
    color: "#0CC418",
    fontSize: "20px",
  };

  const handlePlanSelect = async (plan: StripePlan) => {
    try {
      await sendPageViewToBackend(`/account/changePlans`, {
        plan: plan.docuclipperPlan.name,
        billingCycle: plan.docuclipperPlan.billingCycle,
        numPages: plan.docuclipperPlan.numPages,
        checkoutStage: "pricingcards",
      });
      onSelect(plan, selectedTierIndices);
    } catch (error) {
      console.error("Failed to track plan selection:", error);
      // Still call onSelect even if tracking fails
      onSelect(plan, selectedTierIndices);
    }
  };

  const handleTierChange = (parentPlan: string, tierIndex: number) => {
    setSelectedTierIndices((prev) => ({
      ...prev,
      [parentPlan]: tierIndex,
    }));
  };

  const renderPricingCard = (group: PlanTierGroup) => {
    const tierIndex = selectedTierIndices[group.parentPlan] || 0;
    const selectedTier = group.tiers[tierIndex];

    if (!selectedTier) return null;

    const selectedPlan = selectedTier.plans[billingCycle];
    if (!selectedPlan) return null;

    const monthlyPrice = getTierPrice(selectedTier, "Monthly");
    const annualPrice = selectedTier.plans.Annually
      ? selectedTier.plans.Annually.amount / 100
      : null;

    const displayPrice =
      billingCycle === "Monthly"
        ? monthlyPrice
        : getTierPrice(selectedTier, billingCycle);

    const currencySymbol = getCurrencySymbol(selectedPlan.currency);

    // Calculate savings
    let savingsPercentage = 0;
    if (billingCycle !== "Monthly" && monthlyPrice && annualPrice) {
      savingsPercentage = calculateSavingsPercentage(monthlyPrice, annualPrice);
    }

    const isPopular = group.parentPlan === "Business";
    const features = getPlanFeatures(group.parentPlan);
    const isEnterprise = group.parentPlan === "Enterprise";

    return (
      <Col
        key={group.parentPlan}
        xs="12"
        sm="12"
        md="12"
        lg="4"
        xl="4"
        className="mb-4"
      >
        <Card
          className={`h-100 pricing-card ${isPopular ? "border-warning" : ""}`}
          style={{
            borderRadius: "10px",
            borderWidth: "2px",
            borderStyle: "solid",
            boxShadow: "0 4px 8px 0 rgba(0, 0, 0, 0.1)",
            position: "relative",
          }}
        >
          <Card.Body className="d-flex flex-column">
            {/* Popular Badge - Floating */}
            {isPopular && (
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  zIndex: 10,
                }}
              >
                <Badge
                  variant="warning"
                  style={{
                    fontSize: "12px",
                    fontWeight: "bold",
                    padding: "5px 15px",
                  }}
                >
                  <FontAwesomeIcon icon="star" className="mr-1" />
                  Best Value
                </Badge>
              </div>
            )}

            {/* Plan Name */}
            <div className="text-center mb-2">
              <h2
                style={{ color: "#3f6ad8", fontWeight: "bold" }}
                className="mb-0"
              >
                {group.displayName}
              </h2>
            </div>

            {/* Best For - Right below plan name */}
            <div className="text-center mb-3 px-3">
              <div className="small text-primary font-weight-bold">
                <FontAwesomeIcon icon="lightbulb" className="mr-1" />
                Best For:
              </div>
              <div className="small" style={{ color: "#495057" }}>
                {getSegmentPlanDescription(user?.segment, group.parentPlan)}
              </div>
            </div>

            {/* Volume Slider */}
            {group.tiers.length > 1 && (
              <div className="mb-4 px-3">
                <Form.Label className="font-weight-bold text-center d-block">
                  Pages per {billingCycle === "Annually" ? "year" : "month"}:{" "}
                  <span style={{ color: "#3f6ad8" }}>
                    {(billingCycle === "Annually"
                      ? selectedTier.numPages * 12
                      : selectedTier.numPages
                    ).toLocaleString()}
                  </span>
                </Form.Label>

                {/* Slider Container with tick marks on top */}
                <div style={{ position: "relative" }}>
                  {/* Tick marks above slider */}
                  <div className="d-flex justify-content-center">
                    <div
                      style={{
                        width: group.tiers.length <= 3 ? "60%" : "80%",
                        position: "relative",
                        height: "8px",
                        marginBottom: "5px",
                      }}
                    >
                      {group.tiers.map((tier, idx) => (
                        <div
                          key={idx}
                          style={{
                            position: "absolute",
                            left: `${(idx / (group.tiers.length - 1)) * 100}%`,
                            transform: "translateX(-50%)",
                            width: "2px",
                            height: "8px",
                            backgroundColor: "#6c757d",
                            top: "0",
                          }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Slider */}
                  <div className="d-flex justify-content-center">
                    <input
                      type="range"
                      className="custom-range"
                      min={0}
                      max={group.tiers.length - 1}
                      step={1}
                      value={tierIndex}
                      onChange={(e) =>
                        handleTierChange(
                          group.parentPlan,
                          parseInt(e.target.value)
                        )
                      }
                      style={{
                        cursor: "pointer",
                        width: group.tiers.length <= 3 ? "60%" : "80%",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Price Display */}
            <div className="text-center mb-4">
              <div
                style={{
                  fontSize: "1.8em",
                  fontWeight: "bold",
                  color: "black",
                }}
              >
                {isEnterprise && "From "}
                {currencySymbol}
                {Math.round(displayPrice || 0)}
                <span style={{ fontSize: "0.6em", color: "#2D374880" }}>
                  /mo
                </span>
              </div>

              {/* {billingCycle !== "Monthly" && savingsPercentage > 0 && (
                <div className="text-success small mt-2">
                  💰 Save {savingsPercentage}% vs monthly
                </div>
              )} */}

              {coupon && (
                <div className="mt-2">
                  <span
                    className="badge badge-danger"
                    style={{ fontSize: "0.9em" }}
                  >
                    {coupon.percent_off}% off
                  </span>
                </div>
              )}
            </div>

            {/* Features */}
            <div
              style={{ minHeight: "300px" }}
              className="text-left plan-features mb-4 flex-grow-1"
            >
              {group.displayName === "Business" && (
                <div className="mb-4">
                  <strong>Everything in Starter plus:</strong>
                </div>
              )}
              {group.displayName === "Enterprise" && (
                <div className="mb-4">
                  <strong>Everything in Business plus:</strong>
                </div>
              )}
              {group.tiers.length === 1 && (
                <p className="font-weight-bold mb-3">
                  <FontAwesomeIcon
                    icon="check-circle"
                    style={checkCircleStyle}
                  />
                  <span className="ml-2">
                    {billingCycle === "Annually"
                      ? (selectedTier.numPages * 12).toLocaleString()
                      : selectedTier.numPages.toLocaleString()}{" "}
                    pages / {billingCycle === "Annually" ? "year" : "month"}
                  </span>
                </p>
              )}

              {features.map((feature, idx) => (
                <p
                  key={idx}
                  className={feature.highlight ? "font-weight-bold" : ""}
                >
                  <FontAwesomeIcon
                    icon="check-circle"
                    style={checkCircleStyle}
                  />
                  <span className="ml-2">{feature.text}</span>
                </p>
              ))}
            </div>

            {/* Enterprise Custom Quote Section - Above CTA button */}
            {isEnterprise && (
              <div
                className="mb-3 p-3 border rounded"
                style={{ backgroundColor: "#f8f9fa" }}
              >
                <div className="small font-weight-bold mb-2 text-center">
                  <FontAwesomeIcon icon="phone" className="mr-2 text-primary" />
                  Need a custom solution?
                </div>
                <div
                  className="small mb-2 text-center"
                  style={{ color: "#495057" }}
                >
                  For custom volume needs, dedicated support, or enterprise
                  features
                </div>
                <Button
                  variant="outline-primary"
                  size="sm"
                  href="https://www.docuclipper.com/contact/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-100"
                >
                  <FontAwesomeIcon icon="external-link-alt" className="mr-2" />
                  Request Custom Quote
                </Button>
              </div>
            )}

            {/* CTA Button */}
            <div className="text-center">
              <Button
                variant="primary"
                size="lg"
                style={{
                  backgroundColor: "#007bff",
                  borderColor: "#007bff",
                  fontWeight: "bold",
                  padding: "10px 30px",
                  borderRadius: "20px",
                }}
                onClick={() => handlePlanSelect(selectedPlan)}
                className="w-100"
              >
                Choose this plan
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  return (
    <div className="text-center justify-content-center p-4">
      {/* Segment-Specific Heading */}
      <div className="mb-4">
        <h2 className="font-weight-bold mb-3">
          {getSegmentHeading(user?.segment)}
        </h2>
      </div>

      {/* Trust Badges */}
      <Row className="justify-content-center mb-4">
        <Col md={10}>
          <div
            className="d-flex justify-content-around align-items-center p-3"
            style={{ backgroundColor: "#f8f9fa", borderRadius: "10px" }}
          >
            <div className="text-center">
              <FontAwesomeIcon
                icon="shield-alt"
                className="text-success mb-1"
                style={{ fontSize: "28px" }}
              />
              <div className="small font-weight-bold">Bank-Level Security</div>
            </div>
            <div className="text-center">
              <FontAwesomeIcon
                icon="star"
                className="text-warning mb-1"
                style={{ fontSize: "28px" }}
              />
              <div className="small font-weight-bold">4.8/5 Rating</div>
              <div className="small font-weight-bold">10,000+ Customers</div>
            </div>
            {/* <div className="text-center">
              <FontAwesomeIcon
                icon="users"
                className="text-primary mb-1"
                style={{ fontSize: "28px" }}
              />
              <div className="small font-weight-bold">Free Unlimited Users</div>
            </div> */}
            <div className="text-center">
              <FontAwesomeIcon
                icon="file-alt"
                className="text-info mb-1"
                style={{ fontSize: "28px" }}
              />
              <div className="small font-weight-bold">
                6M+ Documents Processed
              </div>
            </div>
            <div className="text-center">
              <FontAwesomeIcon
                icon="times-circle"
                className="text-success mb-1"
                style={{ fontSize: "28px" }}
              />
              <div className="small font-weight-bold">Cancel Anytime</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* Billing Cycle Toggle */}
      <Row className="justify-content-center align-items-center m-4">
        <Button
          className={`billing-button ${
            billingCycle === "Annually" ? "active" : "inactive"
          }`}
          onClick={() => setBillingCycle("Annually")}
          style={{
            backgroundColor: billingCycle === "Annually" ? "#007bff" : "#fff",
            color: billingCycle === "Annually" ? "#fff" : "#007bff",
            border: "2px solid #007bff",
            fontWeight: "bold",
            padding: "10px 20px",
            borderRadius: "5px",
          }}
        >
          Annually (Save 30%)
        </Button>
        <Button
          className={`billing-button ${
            billingCycle === "Monthly" ? "active" : "inactive"
          } ml-2`}
          onClick={() => setBillingCycle("Monthly")}
          style={{
            backgroundColor: billingCycle === "Monthly" ? "#007bff" : "#fff",
            color: billingCycle === "Monthly" ? "#fff" : "#007bff",
            border: "2px solid #007bff",
            fontWeight: "bold",
            padding: "10px 20px",
            borderRadius: "5px",
          }}
        >
          Monthly
        </Button>
      </Row>

      {/* Pricing Cards */}
      <Row className="justify-content-center">
        {planTierGroups.map((group) => renderPricingCard(group))}
      </Row>

      {planTierGroups.length === 0 && (
        <Alert variant="warning" className="mt-4">
          No plans available at this time.
        </Alert>
      )}

      {/* Page Counter Tool */}
      <Row className="justify-content-center mt-5">
        <Col lg={10}>
          <PDFPageCounter />
        </Col>
      </Row>

      {/* FAQs Section */}
      <Row className="justify-content-center mt-5">
        <Col lg={10}>
          <Card className="shadow-sm">
            <Card.Body className="p-4">
              <h4 className="mb-4 text-center font-weight-bold">
                Frequently Asked Questions
              </h4>
              <Row>
                <Col md={6}>
                  <div className="mb-4">
                    <h6 className="font-weight-bold">
                      <FontAwesomeIcon
                        icon="question-circle"
                        className="mr-2 text-primary"
                      />
                      How do tiers work?
                    </h6>
                    <p className="small mb-0" style={{ color: "#495057" }}>
                      Each plan offers multiple volume tiers. Use the slider to
                      select the page count that matches your needs. All
                      features remain the same within each plan—only the page
                      volume changes.
                    </p>
                  </div>
                  <div className="mb-4">
                    <h6 className="font-weight-bold">
                      <FontAwesomeIcon
                        icon="question-circle"
                        className="mr-2 text-primary"
                      />
                      What happens if I exceed my page limit?
                    </h6>
                    <p className="small mb-0" style={{ color: "#495057" }}>
                      Don't worry! You'll be charged a small per-page overage
                      fee. There's no service interruption, and you can upgrade
                      to a higher tier anytime.
                    </p>
                  </div>
                  <div className="mb-4">
                    <h6 className="font-weight-bold">
                      <FontAwesomeIcon
                        icon="question-circle"
                        className="mr-2 text-primary"
                      />
                      Can I change plans or tiers later?
                    </h6>
                    <p className="small mb-0" style={{ color: "#495057" }}>
                      Absolutely! You can upgrade, downgrade, or switch between
                      tiers at any time from your account settings. Changes take
                      effect immediately.
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-4">
                    <h6 className="font-weight-bold">
                      <FontAwesomeIcon
                        icon="question-circle"
                        className="mr-2 text-primary"
                      />
                      What's the difference between monthly and annual billing?
                    </h6>
                    <p className="small mb-0" style={{ color: "#495057" }}>
                      Annual billing gives you up to 30% savings and provides
                      your full year's page allocation upfront. Monthly billing
                      renews each month. Both options include the same features.
                    </p>
                  </div>
                  <div className="mb-4">
                    <h6 className="font-weight-bold">
                      <FontAwesomeIcon
                        icon="question-circle"
                        className="mr-2 text-primary"
                      />
                      Are there any setup fees or hidden charges?
                    </h6>
                    <p className="small mb-0" style={{ color: "#495057" }}>
                      No setup fees, no hidden charges. You only pay for the
                      plan you select plus any overages if you exceed your page
                      limit. Cancel anytime with no penalties.
                    </p>
                  </div>
                  <div className="mb-4">
                    <h6 className="font-weight-bold">
                      <FontAwesomeIcon
                        icon="question-circle"
                        className="mr-2 text-primary"
                      />
                      Do unused pages roll over?
                    </h6>
                    <p className="small mb-0" style={{ color: "#495057" }}>
                      For annual plans, all pages are available upfront to use
                      anytime during your year. For monthly plans, unused pages
                      don't roll over—choose a tier that matches your expected
                      usage.
                    </p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Customer Testimonials */}
      <Row className="justify-content-center mt-5">
        <Col lg={12}>
          <h4 className="mb-4 text-center font-weight-bold">
            What Our Customers Say
          </h4>

          <Row className="justify-content-center">
            {testimonials.map((testimonial, idx) => (
              <Col key={idx} lg={4} md={6} className="mb-4">
                <Card className="h-100 shadow-sm">
                  <Card.Body className="d-flex flex-column">
                    {/* Stars */}
                    <div className="text-center mb-3">
                      {[...Array(5)].map((_, i) => (
                        <FontAwesomeIcon
                          key={i}
                          icon="star"
                          className="text-warning"
                          style={{ fontSize: "16px", marginRight: "3px" }}
                        />
                      ))}
                    </div>

                    {/* Quote */}
                    <div className="mb-2 text-center">
                      <h6
                        className="font-weight-bold"
                        style={{ color: "#3f6ad8" }}
                      >
                        {testimonial.quote}
                      </h6>
                    </div>

                    {/* Text */}
                    <div className="mb-3 flex-grow-1">
                      <p className="small" style={{ color: "#495057" }}>
                        {testimonial.text}
                      </p>
                    </div>

                    {/* Link */}
                    {testimonial.link && (
                      <div className="mb-3 text-center">
                        <a
                          href={testimonial.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="small text-primary font-weight-bold"
                        >
                          Read Customer Story →
                        </a>
                      </div>
                    )}

                    {/* Author */}
                    <div className="d-flex align-items-center mt-auto">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        style={{
                          width: "50px",
                          height: "50px",
                          borderRadius: "50%",
                          objectFit: "cover",
                          marginRight: "12px",
                        }}
                      />
                      <div>
                        <div className="small font-weight-bold">
                          {testimonial.name}
                        </div>
                        <div className="small" style={{ color: "#6c757d" }}>
                          {testimonial.title}
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default connect((state) => ({}), { createAlert })(
  withRouter(PlanSelector2)
);
